'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function LandingTopbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change / resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navLinks = [
    { label: 'What is Uyir', href: '#what-is-uyir' },
    { label: 'How it Works', href: '#how-it-works' },
    { label: 'Features', href: '#features' },
    { label: 'Privacy', href: '#privacy' },
    { label: 'Pricing', href: '/pricing' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen ? 'glass-strong border-b border-[var(--glass-border)]' : 'bg-transparent'
      }`}
      role="banner"
    >
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-xl gradient-text tracking-tight" aria-hidden="true">Uyir</span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6" aria-label="Landing page navigation">
          {navLinks?.map((item) => (
            <a
              key={`landing-nav-${item?.label?.replace(/\s/g, '-')?.toLowerCase()}`}
              href={item?.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 rounded-sm"
            >
              {item?.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Desktop auth buttons */}
          <div className="hidden md:flex items-center gap-3">
            {!loading && (
              user ? (
                <Link
                  href="/home-chat-interface"
                  className="px-5 py-2.5 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                >
                  Go to App
                </Link>
              ) : (
                <>
                  <Link
                    href="/sign-up-login-screen?tab=login"
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 rounded-sm"
                  >
                    Sign in
                  </Link>
                  <Link
                    href="/sign-up-login-screen?tab=register"
                    className="px-5 py-2.5 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                  >
                    Sign up
                  </Link>
                </>
              )
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl hover:bg-primary/5 transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {mobileMenuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {/* Mobile nav dropdown */}
      {mobileMenuOpen && (
        <div
          id="mobile-nav"
          className="md:hidden glass-strong border-t border-[var(--glass-border)] px-6 py-4 flex flex-col gap-1 fade-in"
          role="navigation"
          aria-label="Mobile navigation"
        >
          {navLinks?.map((item) => (
            <a
              key={`mobile-nav-${item?.label?.replace(/\s/g, '-')?.toLowerCase()}`}
              href={item?.href}
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 text-sm text-foreground hover:text-primary transition-colors duration-150 border-b border-[var(--glass-border)] last:border-b-0 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 rounded-sm"
            >
              {item?.label}
            </a>
          ))}
          {!loading && (
            <div className="pt-3 flex flex-col gap-2">
              {user ? (
                <Link
                  href="/home-chat-interface"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full text-center px-5 py-3 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 transition-all focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                >
                  Go to App
                </Link>
              ) : (
                <>
                  <Link
                    href="/sign-up-login-screen?tab=login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full text-center px-5 py-3 rounded-xl glass border border-[var(--glass-border)] text-sm font-medium hover:bg-primary/5 transition-all focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
                  >
                    Sign in
                  </Link>
                  <Link
                    href="/sign-up-login-screen?tab=register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full text-center px-5 py-3 rounded-xl gradient-bg text-white text-sm font-semibold hover:opacity-90 transition-all focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
}